import React from "react";
import { isChunkLoadError } from "./lazyWithRetry";

/*
 * ChunkErrorBoundary — red de seguridad para la carga de pantallas lazy.
 *
 * Sin un ErrorBoundary, si un componente lazy no resuelve (chunk que no baja),
 * el <Suspense> se queda en el spinner para siempre o la app entera se rompe.
 * Este boundary captura ese fallo (y cualquier error de render del árbol de
 * rutas) y muestra un mensaje claro + un botón para recargar, en lugar de
 * dejar al usuario colgado.
 *
 * Solo se activa cuando un hijo TIRA un error durante el render; si todo carga
 * bien es 100% transparente. Antes NO había ningún boundary, así que esto solo
 * puede mejorar la situación actual (spinner eterno / pantalla en blanco).
 *
 * Reset al navegar: recibe la ruta actual por prop `location`. Si el usuario
 * está viendo la pantalla de error y cambia de ruta (p.ej. tocando el AppBar),
 * limpiamos el estado de error así la nueva pantalla se muestra en vez de quedar
 * pegado en el error. Importante: NO reseteamos por `key`, para no remontar los
 * providers de Reservas/Caja/Comandas que persisten a propósito entre subrutas.
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    // hasError es un booleano separado del valor: si un hijo tirara un valor
    // "falsy" (throw null/undefined/""), un chequeo por verdad del error dejaría
    // pasar los children y volvería a tirar. El flag evita ese borde.
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log para diagnóstico. Envuelto por las dudas: nunca debe romper el render
    // del fallback.
    try {
      // eslint-disable-next-line no-console
      console.error("[ChunkErrorBoundary]", error, info);
    } catch (_) {
      /* no-op */
    }
  }

  componentDidUpdate(prevProps) {
    // Si estábamos mostrando el error y el usuario navegó a otra ruta, limpiamos
    // el error para que la nueva pantalla se renderice.
    if (this.state.hasError && prevProps.location !== this.props.location) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReload = () => {
    // Recargamos a mano. NO tocamos el guard de rescate: si lo borráramos, al
    // recargar en la misma URL rota se dispararía OTRA recarga automática de
    // rescate (doble recarga por click). Dejándolo, la recarga manual da un
    // intento fresco y, si vuelve a fallar, se muestra el mensaje directo.
    try {
      window.location.reload();
    } catch (_) {
      /* no-op */
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isChunk = isChunkLoadError(this.state.error);
    const title = isChunk
      ? "No pudimos cargar esta sección"
      : "Ocurrió un error al mostrar esta sección";
    const detail = isChunk
      ? "Suele ser un problema de conexión. Revisá tu internet y recargá la página."
      : "Probá recargar la página. Si sigue pasando, avisanos.";

    const wrap = {
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "24px",
      boxSizing: "border-box",
      fontFamily:
        "'Urbanist', -apple-system, BlinkMacSystemFont, sans-serif",
    };
    const btn = {
      marginTop: "20px",
      background: "#FDC029",
      color: "#111",
      border: 0,
      borderRadius: "12px",
      padding: "14px 28px",
      fontSize: "16px",
      fontWeight: 700,
      cursor: "pointer",
    };

    return (
      <div style={wrap}>
        <div style={{ fontSize: "48px", lineHeight: 1, marginBottom: "12px" }}>
          📡
        </div>
        <h1 style={{ fontSize: "20px", margin: "0 0 10px", color: "#222" }}>
          {title}
        </h1>
        <p
          style={{
            color: "#666",
            lineHeight: 1.6,
            maxWidth: "340px",
            margin: 0,
            fontSize: "15px",
          }}
        >
          {detail}
        </p>
        <button type="button" style={btn} onClick={this.handleReload}>
          Recargar
        </button>
      </div>
    );
  }
}

export default ChunkErrorBoundary;
